import { MapPin, CheckCircle2, ChevronRight, Calendar, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const HeroBadges = ({ items }) => (
  <div className="flex flex-wrap gap-6">
    {items.map((item) => (
      <div key={item.label} className="flex items-center gap-2 text-[#1A2332]">
        <CheckCircle2 className="w-5 h-5 text-[#C89D4B]" />
        <span className="font-medium">{item.label}</span>
      </div>
    ))}
  </div>
);

const HeroImage = () => (
  <div className="relative animate-fadeInUp animation-delay-200">
    <div className="relative z-10">
      <div className="rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white">
        <img
          src="/images/hero_caregiver.jpg"
          alt="Professional caregiver smiling with elderly patient at home"
          className="w-full h-[500px] object-cover"
          data-testid="hero-image"
        />
      </div>
      <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-[#C89D4B]/20 animate-float">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-[#003A4B] to-[#1B5E6B] rounded-full flex items-center justify-center">
            <Award className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-bold text-[#1A2332]">10+ Years</p>
            <p className="text-sm text-[#718096]">Experience</p>
          </div>
        </div>
      </div>
    </div>
    <div className="absolute top-8 -right-8 w-full h-full bg-gradient-to-br from-[#003A4B]/15 to-[#C89D4B]/15 rounded-[2rem] -z-10"></div>
  </div>
);

export const HeroSection = () => {
  const { t } = useLanguage();

  const badges = [
    { label: t.hero.certified },
    { label: t.hero.experience },
    { label: t.hero.lifeSupport },
    { label: t.hero.dementiaCare },
  ];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-[#F3F0E7] via-white to-[#F8F5EE] relative overflow-hidden"
    >
      <div className="absolute top-20 right-0 w-96 h-96 bg-[#003A4B]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#C89D4B]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-8 animate-fadeInUp">
            <div className="inline-flex items-center gap-2 bg-[#003A4B]/10 text-[#003A4B] px-4 py-2 rounded-full text-sm font-medium">
              <MapPin className="w-4 h-4" />
              {t.hero.location}
            </div>

            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A2332] leading-tight">
              {t.hero.title1}{" "}
              <span className="text-[#003A4B] relative">
                {t.hero.title2}
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                  <path d="M2 10C50 2 150 2 198 10" stroke="#C89D4B" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </span>{" "}
              {t.hero.title3}
            </h1>

            <p className="text-lg text-[#4A5568] leading-relaxed max-w-xl">{t.hero.description}</p>
            <HeroBadges items={badges} />

            <div className="flex flex-wrap gap-4 pt-4">
              <a href="#calculator" data-testid="hero-quote-btn">
                <Button className="bg-[#003A4B] hover:bg-[#002A38] text-white rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all">
                  {t.hero.getQuote}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="#booking" data-testid="hero-book-btn">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-2 border-[#C89D4B] text-[#C89D4B] hover:bg-[#C89D4B]/5 transition-all">
                  <Calendar className="w-5 h-5 mr-2" />
                  {t.hero.bookNow}
                </Button>
              </a>
            </div>
          </div>
          <HeroImage />
        </div>
      </div>
    </section>
  );
};
